import { fromWebToken } from "@aws-sdk/credential-providers";
import { getDefaultRoleAssumerWithWebIdentity } from "@aws-sdk/client-sts";
import {AuthApi} from '@/common/utils/auth-api';

import { CredentialProvider, Credentials } from "@aws-sdk/types";
import { S3Client, S3ClientConfig, PutObjectCommandInput, BucketLoggingStatus } from "@aws-sdk/client-s3";
import { LambdaClient, InvokeCommand, InvokeCommandOutput } from "@aws-sdk/client-lambda";
import { FetchHttpHandler } from "@smithy/fetch-http-handler";
import { Upload } from "@aws-sdk/lib-storage";
import XhrHttpHandler from "./xhr_http_handler";
import type { ProgressCallback, IAwsUserContext } from './aws-types';
import dbg from "../diagnostics";

export class AwsUtils {

    public static credentials: CredentialProvider;
    public static defaultRegion:string;   

    public static readonly INVALID_S3KEY_CHARS: any[] = [...'"<>|\x00\x01\x02\x03\x04\x05\x06\x07\b\t\n\v\f\r\x0E\x0F\x10\x11\x12\x13\x14\x15\x16\x17\x18\x19\x1A\x1B\x1C\x1D\x1E\x1F:*?\\/<>*%&:\\?'];
    
    public static readonly DEFAULT_AWS_REGION = 'us-west-2';
    public static readonly DEFAULT_TOKEN_DURATION = 28800;

    public static async setupCredentials(
        //roleArn: string, idToken: string,userName: string, duration: number = 28800, region: string = "us-west-2"
        userContext: IAwsUserContext):Promise<Credentials> {

        // Fill in any missing bits with defaults
        if (!userContext.region) userContext.region = this.DEFAULT_AWS_REGION;
        if (!userContext.tokenDuration) userContext.tokenDuration = this.DEFAULT_TOKEN_DURATION;
        if (!this.defaultRegion) this.defaultRegion = userContext.region;

        /* old
        this.credentials = fromWebToken({
            roleArn: userContext.roleArn,
            roleSessionName: userContext.userName,
            clientConfig: { region: userContext.region },
            // The token passed from the server
            webIdentityToken: userContext.idToken,
            // How long this token should last
            durationSeconds: userContext.tokenDuration,
            roleAssumerWithWebIdentity: getDefaultRoleAssumerWithWebIdentity({
                region: userContext.region
            })
        });
        */

        
        // Create our own function to pass into the credentials
        // argument. This function will get called when credentials
        // are to be fetched. This allows us to intercept the failure
        // when the token has expired.
        const wrappedCredentials = (() => {

            let result:Credentials|null = null;

            return async () => {
                if (result != null) {
                    // If we've previously retrieved an AWS token and it hasn't yet expired, use that token.
                    if (result.expiration && result.expiration.getTime() > new Date().getTime()) {
                        return result;
                    }
                }

                const credentialsArgs = {
                    roleArn: userContext.roleArn,
                    roleSessionName: userContext.userName,                    
                    webIdentityToken: userContext.idToken,
                    durationSeconds: userContext.tokenDuration,
                    clientConfig: { region: userContext.region },                                    
                    roleAssumerWithWebIdentity:getDefaultRoleAssumerWithWebIdentity({region: userContext.region})
                };

                const credentialsFunc = fromWebToken(credentialsArgs);

                try {
                    result = await credentialsFunc();
                } catch (ex) {
                    const refreshResult = await AuthApi.refreshToken();

                    // Update the ID Token with the one we just received after refreshing our tokens.
                    userContext.idToken = refreshResult.data;

                    // We will have received a new ID token.
                    credentialsArgs.webIdentityToken = userContext.idToken;

                    // Try one last time. If this fails, then the failure is for some other reason
                    result = await fromWebToken(credentialsArgs)();
                }

                return result;
            };
        })();

        this.credentials = wrappedCredentials;
    
        return await this.credentials();
        //return this.credentials;
    }
    

    public static internalCreateS3Client(region: string = this.DEFAULT_AWS_REGION, httpHandler:FetchHttpHandler): S3Client {
       
        const s3Config: S3ClientConfig = {
            region: region,
            credentials: this.credentials,
            useAccelerateEndpoint: true,
            maxAttempts: 10,
            requestHandler: httpHandler,
        };

        return new S3Client(s3Config);
    }

    public static createS3Client(region: string = this.DEFAULT_AWS_REGION):S3Client{
        const httpHandler = new FetchHttpHandler({
            // From v2 - Set timeout to 0 for slow connections as per https://github.com/aws/aws-sdk-js/issues/1704 and various other sources
            requestTimeout: 0
        });

        return this.internalCreateS3Client(region, httpHandler);
    }

   

    public static createLambdaClient(region = "us-west-2"):LambdaClient{
        const lambdaClient = new LambdaClient({
            credentials: AwsUtils.credentials,
            region: region
        });

        return lambdaClient;
    }

    public static sanitizeKeyName(keyname:string){
        for (const invalidCharIdx in this.INVALID_S3KEY_CHARS) {
            const invalidChar = this.INVALID_S3KEY_CHARS[invalidCharIdx];
            while (keyname.indexOf(invalidChar) > -1) {
                keyname = keyname.replace(invalidChar, '_');
            }
        }

        return keyname;
    }

    /*
    public static testUpload(ctx: IUploadClientContext) {
        var params: PutObjectCommandInput = {
            Bucket: ctx.targetS3Bucket,
            Key: 'uploads/' + ctx.userName + '/' + "test.txt",
            Body: new TextEncoder().encode("HELLO")
        };
        const upload = this.createUpload(params, (progress) => {console.log(progress);});

        upload.done().then(x => {
            console.log(x);
        })
            .catch(reason => {
                console.log(reason);
            })
            .finally(() => {
                console.log("finally");
            })
    }
*/
    public static createUpload(params: PutObjectCommandInput, listener: (progress: any) => void):Upload{

        // Ponyfill Blob.stream if necessary (Safari 14.1)
        if (!Blob.prototype.stream) {
            params.Body = this.blobStream(params.Body);
            dbg.addBreadcrumb({ type: "browser-fix", message:"Using polyfill stream"});           
        }

        const upload = new Upload({
            client: this.createS3Client(this.defaultRegion),
            params: params,
            // This will prevent the DELETE request from occuring after canceling. 
            // We're bypassing it because our CORS policy doesn't allow DELETE
            leavePartsOnError: true,

            queueSize:1
        });

      
        upload.on('httpUploadProgress', listener);        
        return upload;
    }

    public static createXhrUpload(params: PutObjectCommandInput, callback: ProgressCallback): Upload {

        // Ponyfill Blob.stream if necessary (Safari 14.1)
        if (!Blob.prototype.stream) {
            params.Body = this.blobStream(params.Body);
            dbg.addBreadcrumb({ type: "browser-fix", message: "Using custom stream" });
        }

        const httpHandler = new XhrHttpHandler({
            // From v2 - Set timeout to 0 for slow connections as per https://github.com/aws/aws-sdk-js/issues/1704 and various other sources
            requestTimeout: 0
        });

        httpHandler.onProgress(callback);

        const upload = new Upload({
            client: this.internalCreateS3Client(this.defaultRegion, httpHandler),
            params: params,
            // This will prevent the DELETE request from occuring after canceling. 
            // We're bypassing it because our CORS policy doesn't allow DELETE
            leavePartsOnError: true,

            queueSize: 1
        });
    
        return upload;
    }

    public static invokeLambda(functionName: string, payloadText: string, cb: (err: any, data?: InvokeCommandOutput) => void)
    {
        const lambdaRequest = new InvokeCommand({
            FunctionName: functionName,
            InvocationType: "RequestResponse",
            Payload: new TextEncoder().encode(payloadText)
        });

        const client = this.createLambdaClient(this.defaultRegion);

        return client.send(lambdaRequest).then(
            data => cb(undefined, data),
            err => cb(err, undefined)
        );
    }

    public static blobStream(blob:any) {
        let position = 0;

        return new ReadableStream({
            async pull(controller) {

                const desiredSize = controller.desiredSize ?? 1000;

                const chunk = blob.slice(
                    position,
                    Math.min(blob.size, position + Math.max(desiredSize, 512 * 1024))
                );
                const buffer = await new Response(chunk).arrayBuffer();
                const uint8array = new Uint8Array(buffer);
                const bytesRead = uint8array.byteLength;

                position += bytesRead;
                controller.enqueue(uint8array);

                if (position >= blob.size) controller.close();
            }
        });
    }
}
